import {
  Alert,
  Button,
  Flex,
  Heading,
  IconInfoSolid,
  RadioInput,
  RadioInputGroup,
  ScreenReaderContent,
  Text,
  ToggleDetails,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Card } from "../components/Card";
import { ChartSubjectSelect } from "../components/Filter/ChartSubjectSelect";
import { AcademicHierarchySelect } from "../components/Selectors/AcademicHierarchySelect/HierarchySelector";
import { CourseLevelSelect } from "../components/Selectors/CourseLevelSelect";
import { ProgramSelect } from "../components/Selectors/ProgramSelect";
import { MULTI, Selector, SINGLE } from "../components/Selectors/Selector";
import VarietyCheckbox from "../components/Selectors/Variety/VarietyCheckbox";
import AcademicYearSelect from "../components/TermManagement/AcademicYears/AcademicYearSelect";
import { TermSelect } from "../components/TermManagement/Terms/TermSelect";
import { CourseService } from "../services/CourseService";
import { OutcomeCategoryService } from "../services/OutcomeCategoryService";
import { TermsContext, UserDetailContext } from "../utils/Context";
import BarChart from "./Cards/Bar";
import LineChart from "./Cards/LineChart";
import OutcomesLink from "./Cards/OutcomesLink";

const ChartData = ({
  showBarChart,
  showLineChart,
  filters,
  trigger,
  setTrigger,
  setFilters,
  inCourse,
  subjects,
  isFacultyDash,
  section,
  assignmentData,
  chartLoad,
  setChartLoad,
  planSubjects,
  selectedAssessmentPlan,
  courseID = null,
}) => {
  const userDetails = useContext(UserDetailContext);
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const [outcomeResultsTitle, setOutcomeResultsTitle] = useState(
    t("dashboard.loResults")
  );
  const [reloadOutcomeResults, setReloadOutcomeResults] = useState(true);
  const [chartTrigger, setChartTrigger] = useState(false);

  const [categories, setCategories] = useState([]);
  const [clearCatAndProgram, setClearCatAndProgram] = useState(false);
  const [acLevelsWithSubjects, setAcLevelsWithSubjects] = useState([]);
  const [hierarchySubjects, setHierarchySubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [acSubClear, setAcSubClear] = useState(false);
  const [barChartWidth, setBarChartWidth] = useState("50%");
  const [resultsViewOptions, setResultsViewOptions] = useState([]);
  const [resultOption, setResultOption] = useState(section?.offering.id ?? "");
  const [offerings, setOfferings] = useState([]);

  const [selectedAcademicYears, setSelectedAcademicYears] = useState([]);
  const [selectedTerms, setSelectedTerms] = useState([]);

  useEffect(() => {
    if (canvasTerms.length > 0) {
      const termList = [];
      filters.terms.forEach((t) => {
        const canvasTerm = canvasTerms.find(
          (canvasTerm) => canvasTerm.canvas_id === t
        );
        if (!termList.includes(canvasTerm.insights_term_id)) {
          termList.push(canvasTerm.insights_term_id);
        }
      });
      setSelectedTerms(termList);
    }
  }, [canvasTerms, filters.terms]);

  const loInputs = [
    { value: "ISLO", label: "ILO" },
    { value: "PSLO", label: "PLO" },
  ];
  if (isFacultyDash || inCourse) {
    loInputs.push({ value: "CSLO", label: "CLO" });
  }

  const measuredAt = [
    { value: "true", label: `${t("dashboard.achievedMastery")} %` },
    { value: "false", label: `${t("dashboard.scoreAvg")} %` },
  ];

  useEffect(() => {
    if (section) {
      setResultsViewOptions([
        {
          value: "null",
          label: `${t("results.dept")} ${section.offering.subject} ${t(
            "results.results"
          )}`,
          typeFilter: "dept",
        },
        {
          value: section && section.course_id ? section.course_id : "",
          label: `${t("results.course")} ${section.course_code} ${t(
            "results.results"
          )}`,
          typeFilter: "course",
        },
        {
          value: section && section.offering ? section.offering.id : "",
          label: `${t("results.offering")}: ${section.offering.code} ${t(
            "results.results"
          )}`,
          typeFilter: "offerings",
        },
      ]);
      if (courseID !== -1) {
        CourseService.getCourseByCanvasId(courseID).then((course) => {
          setOfferings(course.offerings);
          if (course.offerings.length === 1) {
            setResultOption(section.course_id);
            setFilters((prevFilters) => ({
              ...prevFilters,
              courses: [section.course_id],
              offerings: [],
            }));
          }
        });
      }
    }
  }, [section, courseID]);

  useEffect(() => {
    if (!inCourse && filters.LOType && filters.LOType === "ISLO") {
      setCategories([]);
      OutcomeCategoryService.getCategoryByType(filters.LOType).then((cat) => {
        const filtered_cats = Array.from(new Set(cat.map(JSON.stringify))).map(
          JSON.parse
        );
        return setCategories(
          filtered_cats.map((c) => ({ id: c.id, label: c.name }))
        );
      });
    }
  }, [filters.LOType]);

  useEffect(() => {
    if (
      !inCourse &&
      filters.LOType &&
      filters.LOType === "PSLO" &&
      filters.subject
    ) {
      let subject = subjects.find((s) => s.title === filters.subject);
      if (!subject) {
        subject = subjects[0];
      }
      setSelectedSubjectId(subject.id);
    }
  }, [filters.LOType, filters.subject, subjects]);

  useEffect(() => {
    if (acSubClear) {
      setAcSubClear(false);
      setFilters((prevFilters) => ({ ...prevFilters, subject: "" }));
    }
  }, [acSubClear, setFilters]);

  useEffect(() => {
    if (trigger) {
      setHierarchySubjects([]);
    }
  }, [trigger]);

  useEffect(() => {
    if (filters.LOType === "CSLO" && resultsViewOptions.length > 0) {
      if (offerings.length > 1) {
        if (
          resultOption === "null" ||
          (resultOption !== section.course_id &&
            resultOption !== section.offering.id)
        ) {
          const offering = resultsViewOptions[2].value;
          setResultOption(offering);
          setFilters((prevFilters) => ({
            ...prevFilters,
            courses: [],
            offerings: [offering],
          }));
        }
      } else {
        if (resultOption !== null) {
          const course = resultsViewOptions[1].value;
          setResultOption(course);
          setFilters((prevFilters) => ({
            ...prevFilters,
            courses: [course],
            offerings: [],
          }));
        }
      }
    }
  }, [filters.LOType, resultsViewOptions, setFilters]);

  const handleHierarchySelect = (hierarchyId, options) => {
    const subjectList = [];
    let level;
    if (options) {
      level = options;
    } else {
      level = acLevelsWithSubjects.find((lvl) => lvl.id === hierarchyId);
    }
    level.children.forEach((lvl) => {
      if (lvl.label === "is academic subject") {
        subjectList.push(lvl.id);
      }
    });
    setHierarchySubjects(subjectList);
    setAcSubClear(true);
  };

  const handleAcademicSubjectSelect = (selectedSubject) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      subject: selectedSubject,
      programs: [],
    }));
    setClearCatAndProgram(true);
  };

  const handleLOTypeChange = (event) => {
    if (setFilters) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        LOType: event.target.value,
        categories: [],
      }));
      setClearCatAndProgram(true);
    }
    if (event.target.value === "CSLO") {
      setFilters((prevFilters) => ({ ...prevFilters, variety: ["outcome"] }));
    }
  };

  const handleResultsViewChange = (e) => {
    if (setFilters) {
      setResultOption(e.target.value);
      setFilters((prevFilters) => ({
        ...prevFilters,
        courses:
          e.target.value === "null" || e.target.value === section.offering.id
            ? []
            : [e.target.value],
        offerings:
          e.target.value === "null" || e.target.value === section.course_id
            ? []
            : [e.target.value],
      }));
    }
  };

  const handleCategorySelect = (categories) => {
    setClearCatAndProgram(false);
    setFilters((prevFilters) => ({ ...prevFilters, categories: categories }));
  };

  const handleProgramSelect = (programs) => {
    setClearCatAndProgram(false);
    setFilters((prevFilters) => ({ ...prevFilters, programs: programs }));
    const programList = [];
    programs.forEach((p) => {
      programList.push(OutcomeCategoryService.getOutcomeCategoriesByContext(p));
    });
    Promise.all(programList).then((cats) => {
      cats = cats.flat();
      const filtered_cats = Array.from(new Set(cats.map(JSON.stringify))).map(
        JSON.parse
      );
      setCategories(
        filtered_cats.map((c) => {
          return { id: c.id, label: c.name };
        })
      );
    });
  };

  const handleResultsTypeChange = (event) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      masteryResults: event.target.value,
    }));
  };

  const handleAcademicYearSelect = (years) => {
    setSelectedAcademicYears(years);
    // Filter out future insights terms
    let filteredInsightsTerms = years.flatMap((year) => year.insights_terms);
    filteredInsightsTerms = filteredInsightsTerms.filter(
      (term) =>
        new Date(term.start_at).setHours(0, 0, 0, 0) <=
        new Date().setHours(0, 0, 0, 0)
    );
    handleSelectedTerms(filteredInsightsTerms.map((t) => t.id));
  };

  const handleSelectedTerms = (terms) => {
    const selectedInsightsTerms = [];
    terms.forEach((term) => {
      selectedInsightsTerms.push(insightsTerms.find((t) => t.id === term));
    });

    const canvasTermIdList = [];
    selectedInsightsTerms.forEach((term) => {
      canvasTermIdList.push(
        ...term.canvas_terms
          .filter((canvasTerm) => canvasTerm.canvas_id !== -1)
          .map((canvasTerm) => canvasTerm.canvas_id)
      );
    });

    setFilters((prevFilters) => ({ ...prevFilters, terms: canvasTermIdList }));
  };

  const handleSelectedCourseLevels = (levels) => {
    setFilters((prevFilters) => ({ ...prevFilters, courseLevels: levels }));
  };

  const handleOutcomeResultsToggleTitle = (_e, expanded) => {
    if (expanded) {
      setReloadOutcomeResults(true);
      setOutcomeResultsTitle(t("dashboard.loResults"));
    } else {
      setOutcomeResultsTitle(t("dashboard.showLoResults"));
    }
  };

  const loTypeDefault = (isFacultyDash) => {
    const defaultLO = isFacultyDash
      ? "CSLO"
      : filters.LOType
        ? filters.LOType
        : userDetails.isAC()
          ? "ISLO"
          : "PSLO";
    return defaultLO;
  };

  useEffect(() => {
    if (trigger) {
      setReloadOutcomeResults(true);
    }
  }, [trigger]);

  return (
    <View>
      <Card style={{ height: "auto", textAlign: "center" }}>
        <ToggleGroup
          themeOverride={{ mediumIconSize: "1rem" }}
          defaultExpanded
          fluidWidth
          iconPosition="start"
          size="medium"
          border={false}
          onToggle={handleOutcomeResultsToggleTitle}
          toggleLabel={outcomeResultsTitle}
          summary={
            <Flex gap="small" wrap="wrap">
              <Flex.Item>
                <Heading as="h2" level="h4">
                  {outcomeResultsTitle}
                </Heading>
              </Flex.Item>
              {!userDetails.isFaculty() && (
                <Flex.Item shouldGrow>
                  <OutcomesLink
                    reload={reloadOutcomeResults}
                    setReload={setReloadOutcomeResults}
                    subject={filters.subject}
                    searchTrigger={trigger || chartTrigger}
                  />
                </Flex.Item>
              )}
            </Flex>
          }
        >
          <div style={{ width: "100px", marginTop: "5px" }}>
            <Text>{t("dashboard.disclaimer")} </Text>
            <Tooltip
              renderTip={
                <div style={{ width: "600px" }}>
                  {t("dashboard.chartDataDisclaimer")}
                </div>
              }
              placement="top"
              on={["click", "hover", "focus"]}
              color="primary-inverse"
            >
              <IconInfoSolid color="brand" />
            </Tooltip>
          </div>
          {userDetails.role && (
            <Flex wrap="wrap" alignItems="start" margin="medium 0 0 0">
              {!!selectedAssessmentPlan &&
                !selectedAssessmentPlan.sections.some(
                  (section) => section.assessments.length > 0
                ) && (
                  <Flex.Item shouldGrow padding="0 small">
                    <Alert>{t("dashboard.noDataWithMeasures")}</Alert>
                  </Flex.Item>
                )}
              {(!selectedAssessmentPlan ||
                (!!selectedAssessmentPlan &&
                  selectedAssessmentPlan.sections.some(
                    (section) => section.assessments.length > 0
                  ))) &&
                showLineChart &&
                !inCourse && (
                  <LineChart
                    filters={filters}
                    trigger={trigger || chartTrigger}
                    showBarChart={showBarChart}
                    setBarChartWidth={setBarChartWidth}
                    isFacultyDash={!!isFacultyDash}
                    chartLoad={chartLoad}
                    setChartLoad={setChartLoad}
                    setChartTrigger={setChartTrigger}
                  />
                )}
              {showLineChart && inCourse && (
                <LineChart
                  size="100%"
                  filters={filters}
                  trigger={trigger || chartTrigger}
                  inAnalyze={false}
                  setTrigger={setTrigger}
                  subs={subjects}
                  showBarChart={showBarChart}
                  setBarChartWidth={setBarChartWidth}
                  isFacultyDash={!!isFacultyDash}
                  assignmentData={assignmentData}
                  section={section}
                  chartLoad={chartLoad}
                  setChartLoad={setChartLoad}
                  setChartTrigger={setChartTrigger}
                />
              )}
              {(!selectedAssessmentPlan ||
                (!!selectedAssessmentPlan &&
                  selectedAssessmentPlan.sections.some(
                    (section) => section.assessments.length > 0
                  ))) &&
                showBarChart && (
                  <BarChart
                    bindto={"ILOChart"}
                    size={{ height: 190 }}
                    padding={15}
                    filters={filters}
                    trigger={trigger || chartTrigger}
                    barChartWidth={showLineChart ? barChartWidth : "100%"}
                    setChartTrigger={setChartTrigger}
                  />
                )}
              <Flex.Item size="100%" padding="small">
                {!chartLoad ? (
                  <Flex
                    size="100%"
                    alignItems="start"
                    justifyItems="space-between"
                    wrap="wrap"
                  >
                    {!inCourse && (
                      <Flex.Item size="225px">
                        <AcademicHierarchySelect
                          type={SINGLE}
                          size="small"
                          selectHandler={handleHierarchySelect}
                          setAcLevelsWithSubjects={setAcLevelsWithSubjects}
                          isChart={true}
                          placeholder={t(
                            "academicHierarchy.hierarchyPlaceholder"
                          )}
                          planSubjects={planSubjects}
                          clear={trigger}
                        />
                        <ChartSubjectSelect
                          selectHandler={handleAcademicSubjectSelect}
                          clear={acSubClear}
                          defaultSubject={
                            filters.subject
                              ? filters.subject
                              : subjects && subjects.length > 0
                                ? subjects[0].title
                                : ""
                          }
                          hierarchySubjects={hierarchySubjects}
                          size="small"
                          placeholder={t(
                            "curriculumMap.academicSubjectPlaceholder"
                          )}
                          planSubjects={planSubjects}
                        />
                      </Flex.Item>
                    )}
                    <Flex.Item>
                      <div style={{ marginBottom: 10 }}>
                        <RadioInputGroup
                          layout="columns"
                          name="loType"
                          defaultValue={loTypeDefault(isFacultyDash)}
                          description={
                            <Text size="small">{t("filter.loType")}</Text>
                          }
                          size="small"
                          onChange={handleLOTypeChange}
                        >
                          {loInputs.map((input) => (
                            <RadioInput
                              key={input.value}
                              value={input.value}
                              label={input.label}
                            />
                          ))}
                        </RadioInputGroup>
                        <div style={{ marginTop: 10 }}>
                          <VarietyCheckbox
                            filters={filters}
                            selectHandler={(v) =>
                              setFilters((prevFilters) => ({
                                ...prevFilters,
                                variety: v,
                              }))
                            }
                          />
                        </div>
                        {isFacultyDash && (
                          <div style={{ marginTop: 10 }}>
                            <RadioInputGroup
                              layout="columns"
                              name={t("results.resultsBy")}
                              value={resultOption}
                              description={
                                <ScreenReaderContent>
                                  {t("results.view")}
                                </ScreenReaderContent>
                              }
                              size="small"
                              onChange={handleResultsViewChange}
                            >
                              {resultsViewOptions.map(
                                (option) =>
                                  (option.typeFilter !== "offerings" ||
                                    offerings.length > 1) && (
                                    <RadioInput
                                      key={option.value}
                                      value={option.value}
                                      label={option.label}
                                      disabled={
                                        option.value === "null" &&
                                        filters.LOType === "CSLO"
                                      }
                                    />
                                  )
                              )}
                            </RadioInputGroup>
                          </div>
                        )}
                      </div>
                    </Flex.Item>
                    {!inCourse && (
                      <Flex.Item>
                        <p
                          style={{
                            margin: 0,
                            padding: 0,
                            marginBottom: 5,
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            textAlign: "left",
                          }}
                        >
                          {filters.LOType === "PSLO"
                            ? t("categories.selectProgramCategory")
                            : t("categories.selectCategory")}
                        </p>
                        {filters.LOType === "PSLO" && (
                          <div style={{ marginBottom: 5 }}>
                            <ProgramSelect
                              label={
                                <ScreenReaderContent>
                                  {t("programs.selectProgramAssistive")}
                                </ScreenReaderContent>
                              }
                              type={MULTI}
                              size="small"
                              selectHandler={handleProgramSelect}
                              placeholder={t(
                                "programs.selectProgramPlaceholder"
                              )}
                              clear={clearCatAndProgram}
                              selectedSubjectId={
                                selectedSubjectId ?? subjects[0].id
                              }
                            />
                          </div>
                        )}
                        <div>
                          <Selector
                            label={
                              <ScreenReaderContent>
                                {t("categories.selectCategoryAssistive")}
                              </ScreenReaderContent>
                            }
                            id="category-select"
                            type={MULTI}
                            size="small"
                            options={categories}
                            selectHandler={handleCategorySelect}
                            placeholder={t(
                              "categories.selectCategoryPlaceholder"
                            )}
                            clear={clearCatAndProgram}
                            interaction={
                              filters.programs?.length > 0 ||
                              filters.LOType === "ISLO"
                            }
                          />
                        </div>
                      </Flex.Item>
                    )}
                    <Flex.Item>
                      <RadioInputGroup
                        layout="stacked"
                        name="measured"
                        defaultValue={
                          filters.masteryResults
                            ? filters.masteryResults
                            : "true"
                        }
                        description={
                          <Text size="small">{t("filter.masteryResults")}</Text>
                        }
                        size="small"
                        onChange={handleResultsTypeChange}
                      >
                        {measuredAt.map((input) => (
                          <RadioInput
                            key={input.value}
                            value={input.value}
                            label={input.label}
                          />
                        ))}
                      </RadioInputGroup>
                    </Flex.Item>
                    <Flex.Item size="200px">
                      <CourseLevelSelect
                        id="lo-filter-course-level-select"
                        selectHandler={handleSelectedCourseLevels}
                        size="small"
                        selectedLevels={filters.courseLevels ?? []}
                      />
                    </Flex.Item>
                    <Flex.Item size="200px">
                      <AcademicYearSelect
                        selectHandler={handleAcademicYearSelect}
                        noFutureYears
                        selectedAcademicYears={selectedAcademicYears.map(
                          (year) => year.id
                        )}
                        size="small"
                      />
                      <div style={{ marginTop: 10 }}>
                        <TermSelect
                          selectHandler={handleSelectedTerms}
                          selectedAcademicYears={selectedAcademicYears}
                          size="small"
                          selectedTerms={selectedTerms}
                          noFutureTerms={true}
                          label={t("terms")}
                        />
                      </div>
                    </Flex.Item>
                    <Flex.Item margin="medium 0 0 0">
                      <Button
                        size="medium"
                        id="apply-filters-button"
                        color="primary"
                        onClick={() => {
                          setChartTrigger(true);
                          setBarChartWidth("50%");
                        }}
                        interaction={filters.subject ? "enabled" : "disabled"}
                      >
                        {t("filter.applyButton")}
                      </Button>
                    </Flex.Item>
                  </Flex>
                ) : (
                  <Skeleton
                    height={144}
                    count={1}
                    style={{ margin: "10px 0" }}
                  />
                )}
              </Flex.Item>
              {isFacultyDash && offerings.length > 1 && (
                <Flex.Item textAlign="start">
                  <ToggleDetails
                    size="small"
                    summary={t("results.courseVsOffering")}
                  >
                    <Text size="small">
                      {t("results.courseVsOfferingDetails")}
                    </Text>
                  </ToggleDetails>
                </Flex.Item>
              )}
            </Flex>
          )}
        </ToggleGroup>
      </Card>
    </View>
  );
};

export default ChartData;
